import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { RootLayout } from '@/layouts/RootLayout';
import { StoreLayout } from '@/layouts/StoreLayout';
import { RouteErrorPage } from '@/pages/errors/RouteErrorPage';

import { AdminRoute } from './AdminRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicOnlyRoute } from './PublicOnlyRoute';

export const router = createBrowserRouter([
  {
    Component: RootLayout,
    errorElement: <RouteErrorPage />,
    children: [
      {
        Component: StoreLayout,
        children: [
          { index: true, lazy: () => import('@/pages/HomePage') },
          { path: 'collection', lazy: () => import('@/pages/CollectionPage') },
          { path: 'product/:slug', lazy: () => import('@/pages/ProductPage') },
          { path: 'about', lazy: () => import('@/pages/AboutPage') },
          { path: 'contact', lazy: () => import('@/pages/ContactPage') },
          { path: 'terms', lazy: () => import('@/pages/TermsPage') },
          { path: 'privacy', lazy: () => import('@/pages/PrivacyPage') },
          { path: 'checkout', lazy: () => import('@/pages/CheckoutPage') },
          // Customer account pages live in the storefront chrome so shoppers
          // keep the navbar and never lose their way back to the website.
          {
            Component: ProtectedRoute,
            children: [
              {
                element: (
                  <div className="mx-auto w-full max-w-5xl px-5 py-12 sm:px-8">
                    <Outlet />
                  </div>
                ),
                children: [
                  { path: 'orders', lazy: () => import('@/pages/OrdersPage') },
                  { path: 'profile', lazy: () => import('@/pages/ProfilePage') },
                ],
              },
            ],
          },
          // Legacy app-shell dashboard is gone; keep the path working.
          { path: 'dashboard', element: <Navigate to="/" replace /> },
        ],
      },
      {
        Component: PublicOnlyRoute,
        children: [
          {
            Component: AuthLayout,
            children: [
              { path: 'login', lazy: () => import('@/pages/LoginPage') },
              { path: 'register', lazy: () => import('@/pages/RegisterPage') },
              { path: 'forgot-password', lazy: () => import('@/pages/ForgotPasswordPage') },
            ],
          },
        ],
      },
      // The panel lives under /core-site/admin rather than a bare /admin so the
      // sign-in screen is not sitting on the path scanners try first.
      { path: 'core-site/admin/login', lazy: () => import('@/pages/admin/AdminLoginPage') },
      {
        Component: AdminRoute,
        children: [
          {
            Component: AdminLayout,
            children: [
              { path: 'core-site/admin', lazy: () => import('@/pages/admin/AdminDashboardPage') },
              {
                path: 'core-site/admin/products',
                lazy: () => import('@/pages/admin/AdminProductsPage'),
              },
              {
                path: 'core-site/admin/products/new',
                lazy: () => import('@/pages/admin/AdminProductFormPage'),
              },
              {
                path: 'core-site/admin/products/:slug/edit',
                lazy: () => import('@/pages/admin/AdminProductFormPage'),
              },
              {
                path: 'core-site/admin/orders',
                lazy: () => import('@/pages/admin/AdminOrdersPage'),
              },
              {
                path: 'core-site/admin/orders/:ref',
                lazy: () => import('@/pages/admin/AdminOrderDetailPage'),
              },
              {
                path: 'core-site/admin/reviews',
                lazy: () => import('@/pages/admin/AdminReviewsPage'),
              },
              {
                path: 'core-site/admin/testimonials',
                lazy: () => import('@/pages/admin/AdminTestimonialsPage'),
              },
              {
                path: 'core-site/admin/settings',
                lazy: () => import('@/pages/admin/AdminSettingsPage'),
              },
              {
                path: 'core-site/admin/account',
                lazy: () => import('@/pages/admin/AdminAccountPage'),
              },
            ],
          },
        ],
      },
      { path: '403', lazy: () => import('@/pages/errors/ForbiddenPage') },
      { path: '*', lazy: () => import('@/pages/errors/NotFoundPage') },
    ],
  },
]);
